import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Car,
  CircuitBoard,
  Cpu,
  Factory,
  FlaskConical,
  Globe2,
  Handshake,
  Landmark,
  Layers,
  LockKeyhole,
  Mail,
  Network,
  Rocket,
  ServerCog,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mockData';

const metaDescription =
  'SALAR Technologies LLC invites strategic partners, pilot customers, qualified investors, licensing partners, and technology-transition organizations to engage around electronics reliability, semiconductor supply-chain intelligence, AI infrastructure assurance, quantum-safe provenance, and accountable agentic decision governance.';

const legalNote =
  'This page is provided for informational and relationship-building purposes only. Nothing on this page constitutes an offer to sell securities, a solicitation to buy securities, investment advice, or a commitment to accept investment. Any financing, if pursued, will be conducted only through appropriate legal documentation and in compliance with applicable federal and state securities laws.';

const glassCard =
  'rounded-3xl border border-cyan-300/15 bg-slate-950/55 shadow-2xl shadow-blue-950/30 backdrop-blur-md';

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-cyan-300/25 bg-cyan-300/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100">
    {children}
  </span>
);

const SectionHeader = ({ eyebrow, title, children }) => (
  <div className="mx-auto mb-12 max-w-4xl text-center">
    {eyebrow && <p className="mb-3 text-sm font-semibold uppercase tracking-[0.26em] text-cyan-300">{eyebrow}</p>}
    <h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl">{title}</h2>
    {children && <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-slate-300 md:text-lg">{children}</p>}
  </div>
);

const ColorCard = ({ icon: Icon, title, children, accent = 'from-cyan-400 to-blue-500' }) => (
  <article className={`${glassCard} group relative overflow-hidden p-6 transition duration-300 hover:-translate-y-1 hover:border-cyan-300/35 motion-reduce:hover:translate-y-0`}>
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`} />
    <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${accent} text-slate-950 shadow-lg`}>
      <Icon size={24} aria-hidden="true" />
    </div>
    <h3 className="mb-3 text-xl font-bold text-white">{title}</h3>
    <p className="leading-7 text-slate-300">{children}</p>
  </article>
);

const OrbitDiagram = () => {
  const labels = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8'];
  const coords = [
    [400, 50], [585, 125], [660, 310], [585, 495],
    [400, 570], [215, 495], [140, 310], [215, 125],
  ];
  return (
    <div className={`${glassCard} overflow-hidden p-6`}>
      <svg viewBox="0 0 800 620" className="h-auto w-full" role="img" aria-labelledby="p1p8-title p1p8-desc">
        <title id="p1p8-title">P1 through P8 patent-to-market orbit</title>
        <desc id="p1p8-desc">An orbit diagram showing eight patent-pending application areas connected to a central assurance intelligence layer.</desc>
        <defs>
          <radialGradient id="orbit-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.75" />
            <stop offset="65%" stopColor="#1d4ed8" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#020617" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="orbit-stroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="45%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#60a5fa" />
          </linearGradient>
        </defs>
        <rect width="800" height="620" rx="34" fill="rgba(2,6,23,0.82)" />
        <g stroke="#1d4ed8" strokeOpacity="0.16">
          {[...Array(9)].map((_, i) => <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="620" />)}
          {[...Array(7)].map((_, i) => <line key={`h${i}`} x1="0" y1={i * 100} x2="800" y2={i * 100} />)}
        </g>
        <ellipse cx="400" cy="310" rx="270" ry="235" fill="none" stroke="url(#orbit-stroke)" strokeWidth="3" strokeOpacity="0.75" />
        <circle cx="400" cy="310" r="132" fill="url(#orbit-core)" />
        <circle cx="400" cy="310" r="82" fill="rgba(15,23,42,0.92)" stroke="#67e8f9" strokeWidth="2" />
        <text x="400" y="300" textAnchor="middle" className="fill-white text-[22px] font-bold">Assurance</text>
        <text x="400" y="328" textAnchor="middle" className="fill-cyan-100 text-[14px] font-semibold">Intelligence Layer</text>
        {coords.map(([x, y], i) => (
          <g key={labels[i]}>
            <line x1="400" y1="310" x2={x} y2={y} stroke="#38bdf8" strokeOpacity="0.28" strokeWidth="2" />
            <circle cx={x} cy={y} r="38" fill="rgba(15,23,42,0.96)" stroke={i === 7 ? '#f59e0b' : '#22d3ee'} strokeWidth="2" />
            <text x={x} y={y + 7} textAnchor="middle" className="fill-white text-[20px] font-bold">{labels[i]}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const engagementCards = [
  ['Strategic Industrial Partners', Building2, 'For companies building or operating electronics, semiconductors, power systems, AI infrastructure, telecom/networking hardware, industrial automation, or critical infrastructure systems.'],
  ['Pilot Customers', Rocket, 'For teams with real reliability, sourcing, provenance, counterfeit-risk, manufacturing-stability, or AI infrastructure assurance problems that need a 30–60 day evidence-driven pilot.'],
  ['Corporate Venture / Deep-Tech Investors', BriefcaseBusiness, 'For investors who understand semiconductor resilience, AI infrastructure reliability, supply-chain intelligence, trusted electronics, and critical-infrastructure assurance.'],
  ['Prime Contractors / Integrators', Landmark, 'For defense, aerospace, cloud, industrial, and federal integrators seeking a focused technical subcontractor or decision-intelligence layer.'],
  ['Licensing and IP Commercialization Partners', Handshake, 'For companies interested in licensing, co-development, field validation, or commercialization of SALAR’s patent-pending assurance methods.'],
  ['Research, Testbed, Grant, and SBIR/STTR Partners', FlaskConical, 'For labs, universities, accelerators, SBIR/STTR collaborators, HPC/testbed partners, and technology-transition groups.'],
];

const marketCards = [
  ['Defense & Aerospace Electronics', ShieldCheck, 'Long-life mission electronics, sustainment, alternate-source analysis, counterfeit/provenance control, supplier visibility, and qualification evidence.', 'from-cyan-400 to-blue-500'],
  ['Semiconductor Manufacturing & Advanced Packaging', Cpu, 'Lower-tier risk intelligence, OSAT and packaging visibility, process-stability reasoning, metrology/process assurance, and yield-confidence decision support.', 'from-amber-300 to-orange-500'],
  ['AI Data Centers & Cloud Infrastructure', ServerCog, 'Power and thermal reliability, accelerator-rack assurance, cooling and energy-risk logic, workload continuity, and infrastructure health scoring.', 'from-blue-300 to-cyan-400'],
  ['Industrial Automation & Critical Infrastructure', Factory, 'Factory control systems, power electronics, logistics systems, grid-adjacent hardware, maritime/rail infrastructure, and long-life electronics.', 'from-emerald-300 to-cyan-500'],
  ['Automotive, EV & Energy Systems', Car, 'Power modules, battery-adjacent controls, charging infrastructure, embedded controllers, thermal stress, provenance, and reliability scoring.', 'from-lime-300 to-emerald-500'],
  ['Telecom, Networking & Edge Infrastructure', Network, 'Multi-lane SoC connectivity, Ethernet profile governance, edge devices, base-station electronics, high-speed interconnects, and resilient communications.', 'from-violet-300 to-blue-500'],
  ['Consumer Electronics & Connected Devices', CircuitBoard, 'Board reliability, product-quality evidence, component provenance, high-volume manufacturing stability, power/thermal risk, and supplier confidence.', 'from-pink-300 to-rose-500'],
  ['Enterprise AI and Regulated Workflows', Layers, 'Decision logs, action settlement, AI workflow governance, auditable outputs, compliance evidence, procurement actions, and controlled automation.', 'from-amber-300 to-cyan-300'],
];

const patentMarketFit = [
  ['P1', 'Board Design Reliability Analysis', 'Turns board reliability review into repeatable, auditable engineering evidence.', 'Defense electronics, consumer electronics, automotive/EV, telecom, industrial systems, datacenter hardware, PCB/EMS ecosystems.'],
  ['P2', 'Profile-Based Ethernet Connectivity / Multi-Lane SoC Connectivity', 'Supports resilient connectivity governance in complex multi-lane electronics.', 'Telecom, networking, edge infrastructure, datacenters, embedded systems, high-speed interconnect platforms.'],
  ['P3', 'Graph-Based Multi-Tier Semiconductor Supply-Chain Risk Scoring', 'Turns hidden supplier and provenance risk into a decision-grade graph layer.', 'Semiconductors, defense, automotive, OEMs, EMS, sourcing, logistics, critical infrastructure.'],
  ['P4', 'Closed-Loop Manufacturing Stability', 'Links process drift, quality evidence, production confidence, and action feedback.', 'Fabs, OSAT, EMS, industrial automation, advanced manufacturing, quality systems, energy systems.'],
  ['P5', 'AI-Certified Lithography / Public-Safe Process Assurance', 'Moves SALAR upstream into process assurance without exposing confidential enabling detail.', 'Semiconductor fabs, lithography ecosystem, metrology, process control, trusted microelectronics, advanced packaging.'],
  ['P6', 'Physical AI Infrastructure Assurance / Power-Thermal Orchestration', 'Positions SALAR in high-growth AI infrastructure reliability and energy-efficiency assurance.', 'AI datacenters, cloud infrastructure, HPC, accelerator racks, liquid cooling, power delivery, energy-aware compute.'],
  ['P7', 'Quantum-Safe Provenance / Cryptographic Provenance Bridging', 'Creates a bridge between provenance, cryptographic trust, and long-life asset verification.', 'Critical infrastructure, defense, supply-chain security, post-quantum trust, air-gapped systems, long-life electronics, secure communications.'],
  ['P8', 'Agentic Action Settlement Governor / Decision-Trace Governance', 'Extends SALAR into accountable AI workflows, action settlement, and auditable decision governance.', 'Enterprise AI, regulated workflows, mission systems, procurement, logistics, legal/compliance operations, AI action-control systems.'],
];

const InvestorRelations = () => {
  const location = useLocation();
  const pageTitle = 'Strategic Partners & Investor Relations';
  const canonical = location.pathname === '/strategic-partners'
    ? 'https://www.salartechnologies.com/strategic-partners'
    : 'https://www.salartechnologies.com/investor-relations';

  const mailtoHref = useMemo(() => {
    const subject = 'Strategic partner or investor briefing inquiry';
    const body = [
      'Please share a strategic partner or investor briefing packet for SALAR Technologies LLC.',
      '',
      'Name:',
      'Organization:',
      'Role / title:',
      'Inquiry type:',
      'Industry interest:',
      '',
      'I understand this inquiry is informational and is not an offer to sell securities or a solicitation to buy securities.',
    ].join('\n');
    return `mailto:${companyInfo.emails.direct}?cc=${companyInfo.emails.general}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = 'Strategic partner briefing inquiry';
    const body = [
      'Strategic partner briefing inquiry for SALAR Technologies LLC',
      '',
      `Name: ${data.get('fullName') || ''}`,
      `Email: ${data.get('email') || ''}`,
      `Organization: ${data.get('organization') || ''}`,
      `Role / title: ${data.get('roleTitle') || ''}`,
      `Inquiry type: ${data.get('inquiryType') || ''}`,
      `Industry interest: ${data.get('industryInterest') || ''}`,
      '',
      'Message:',
      data.get('message') || '',
      '',
      'Acknowledgement: I understand this is an inquiry-only form. It is not an offer to sell securities or a solicitation to buy securities.',
    ].join('\n');
    window.location.href = `mailto:${companyInfo.emails.direct}?cc=${companyInfo.emails.general}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#06101f] text-white">
      <Helmet>
        <title>{`${pageTitle} | SALAR Technologies LLC`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${pageTitle} | SALAR Technologies LLC`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://www.salartechnologies.com${location.pathname}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.salartechnologies.com/img/salar_hero_semiconductor_resilience.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${pageTitle} | SALAR Technologies LLC`} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>

      <section className="relative isolate min-h-[86vh] overflow-hidden border-b border-cyan-300/10">
        <img
          src="/img/salar_hero_semiconductor_resilience.webp"
          alt="Semiconductor wafer and advanced electronics imagery representing SALAR assurance intelligence"
          className="absolute inset-0 -z-20 h-full w-full object-cover opacity-55"
          loading="eager"
        />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.28),transparent_32%),radial-gradient(circle_at_80%_10%,rgba(245,158,11,0.18),transparent_30%),linear-gradient(120deg,rgba(2,6,23,0.98),rgba(7,22,45,0.86)_48%,rgba(2,6,23,0.95))]" />
        <div className="absolute inset-0 -z-10 animated-grid opacity-70 motion-reduce:animate-none" />

        <div className="container mx-auto flex min-h-[86vh] items-center px-6 py-24">
          <div className="grid w-full items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="max-w-4xl">
              <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">
                <Sparkles size={15} aria-hidden="true" /> Strategic inquiry page for qualified investor, partner, and pilot-customer conversations.
              </p>
              <h1 className="text-5xl font-black tracking-tight text-white md:text-7xl lg:text-8xl">
                {pageTitle}
              </h1>
              <p className="mt-7 max-w-3xl text-lg leading-9 text-slate-200 md:text-2xl">
                SALAR Technologies LLC invites strategic partners, qualified investors, pilot customers, technology-transition organizations, licensing partners, and industry collaborators to engage around reliability, provenance, supply-chain intelligence, manufacturing stability, AI infrastructure assurance, quantum-safe trust, and accountable agentic decision governance.
              </p>
              <p className="mt-5 max-w-3xl text-base leading-8 text-cyan-100 md:text-lg">
                SALAR Technologies LLC is building an assurance intelligence layer for mission-critical electronics, semiconductor supply chains, AI infrastructure, industrial systems, consumer technology platforms, and accountable AI workflows.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Badge>Eight USPTO patent filings</Badge>
                <Badge>Founder-led Oregon technology company</Badge>
                <Badge>Defense + commercial + consumer electronics scope</Badge>
                <Badge>Strategic partner and qualified investor inquiry path</Badge>
              </div>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-cyan-400 px-7 text-slate-950 hover:bg-cyan-300">
                  <a href="#strategic-fit">Discuss Strategic Fit <ArrowRight className="ml-2" size={18} aria-hidden="true" /></a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-cyan-300/40 bg-white/5 px-7 text-cyan-100 hover:bg-cyan-300/10">
                  <a href="#patent-market-map">View Patent-to-Market Map</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-amber-300/40 bg-white/5 px-7 text-amber-100 hover:bg-amber-300/10">
                  <a href="#strategic-inquiry">Request Partner Briefing</a>
                </Button>
              </div>
            </div>

            <div className={`${glassCard} relative overflow-hidden p-6`}>
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-cyan-400/20 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-amber-300/15 blur-3xl" />
              <OrbitDiagram />
              <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-sm leading-7 text-slate-300">
                  A market expansion path from defense electronics into commercial hardware, industrial systems, consumer technology platforms, AI infrastructure, and accountable automation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-14">
        <div className="container mx-auto">
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {[
              ['/img/circuit_chip.jpg', 'Circuit board and semiconductor close-up representing electronics assurance', 'Board and component reliability'],
              ['/img/data_center_servers.jpg', 'Data center server racks representing AI infrastructure reliability', 'AI infrastructure assurance'],
              ['/img/pexels_fiber_cables.jpg', 'Fiber-optic interconnect cables representing high-speed network hardware', 'Telecom and interconnects'],
              ['/img/salar_mission_continuity.webp', 'Abstract mission continuity visual representing provenance and operational resilience', 'Provenance and continuity'],
            ].map(([src, alt, label]) => (
              <figure key={src} className="group overflow-hidden rounded-3xl border border-cyan-300/15 bg-slate-950/55 shadow-xl shadow-blue-950/20">
                <img src={src} alt={alt} className="h-44 w-full object-cover opacity-85 transition duration-300 group-hover:scale-105 group-hover:opacity-100" loading="lazy" />
                <figcaption className="border-t border-cyan-300/10 px-4 py-3 text-sm font-semibold text-cyan-100">{label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="strategic-fit" className="px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Engagement" title="Who Should Engage With SALAR" />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {engagementCards.map(([title, Icon, copy], i) => (
              <ColorCard key={title} icon={Icon} title={title} accent={['from-cyan-400 to-blue-500','from-blue-400 to-indigo-500','from-amber-300 to-orange-500','from-emerald-300 to-cyan-500','from-violet-300 to-fuchsia-500','from-lime-300 to-emerald-500'][i]}>{copy}</ColorCard>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-cyan-300/10 bg-slate-950/35 px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Market map" title="Where SALAR Fits">
            SALAR’s assurance logic is not limited to defense. The same core problems appear across commercial, industrial, infrastructure, and consumer technology markets.
          </SectionHeader>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {marketCards.map(([title, Icon, copy, accent]) => (
              <ColorCard key={title} icon={Icon} title={title} accent={accent}>{copy}</ColorCard>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Models" title="Strategic Partnership Models" />
          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {[
              ['30–60 Day Technical Pilot', 'Risk map, P1–P8 health profile, evidence log, ranked issues, mitigation recommendations, and executive summary.'],
              ['Licensing / IP Evaluation', 'Patent-to-product fit review, licensing discussion, integration scope, protected-method mapping, and commercialization options.'],
              ['Prime / Integrator Teaming', 'Focused subcontracting support for reliability, provenance, supply-chain risk, manufacturing stability, AI infrastructure assurance, or decision governance.'],
              ['Corporate Co-Development', 'Joint validation, industry data model, workflow prototype, targeted customer use case, and commercialization roadmap.'],
              ['Grant / SBIR / Testbed Collaboration', 'White paper, technical narrative, pilot design, test plan, compute/testbed alignment, and transition path.'],
              ['Strategic Investment Discussion', 'Qualified investor conversation, public-safe deck review, roadmap discussion, and legal-documentation path if financing is pursued.'],
            ].map(([title, copy]) => (
              <article key={title} className={`${glassCard} p-6`}>
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <p className="mt-3 text-sm font-semibold uppercase tracking-[0.18em] text-cyan-300">Output</p>
                <p className="mt-2 leading-7 text-slate-300">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="patent-market-map" className="border-y border-cyan-300/10 bg-gradient-to-b from-slate-950/20 to-blue-950/35 px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHeader eyebrow="P1–P8" title="Patent Portfolio to Market Fit">
                SALAR’s eight public patent-pending application areas map to multiple industry wedges, not one narrow market.
              </SectionHeader>
              <OrbitDiagram />
            </div>
            <div className="grid gap-4">
              {patentMarketFit.map(([lane, title, relevance, markets]) => (
                <article key={lane} className="rounded-2xl border border-cyan-300/15 bg-slate-950/55 p-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-xl bg-cyan-300 px-3 py-1 text-sm font-black text-slate-950">{lane}</span>
                    <h3 className="text-lg font-bold text-white">{title}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-300"><strong className="text-cyan-200">Partner value:</strong> {relevance}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-400"><strong className="text-amber-200">Best-fit markets:</strong> {markets}</p>
                </article>
              ))}
              <Button asChild className="mt-4 bg-cyan-400 text-slate-950 hover:bg-cyan-300">
                <a href="#strategic-inquiry">Discuss Strategic Fit <ArrowRight className="ml-2" size={18} aria-hidden="true" /></a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Why it matters" title="Why Partners Should Care" />
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[
              ['Evidence Instead of Buzzwords', 'SALAR converts engineering, sourcing, provenance, manufacturing, and operational signals into explainable decision outputs.', CheckIcon],
              ['Capital-Efficient Technical Wedge', 'SALAR does not need to build fabs or become a hardware OEM to create value. It fits at the decision, evidence, and assurance layer.', Layers],
              ['Defense-to-Commercial Expansion', 'The same assurance logic supports defense electronics, AI infrastructure, consumer electronics, industrial systems, automotive, energy, telecom, and enterprise AI governance.', Globe2],
              ['Patent-Pending Platform Logic', 'Eight USPTO patent filings support a broader assurance architecture across reliability, supply-chain risk, manufacturing stability, AI infrastructure, quantum-safe provenance, and agentic decision governance.', Zap],
            ].map(([title, copy, Icon]) => (
              <ColorCard key={title} icon={Icon} title={title}>{copy}</ColorCard>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-cyan-300/10 bg-slate-950/45 px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Path" title="How a Strategic Conversation Starts" />
          <div className="grid gap-5 md:grid-cols-4">
            {[
              ['1', 'Fit Screening', 'Identify industry, pain point, data availability, and partnership type.'],
              ['2', 'Public-Safe Briefing', 'Share SALAR’s public deck, demo, patent-to-market map, and partner fit summary.'],
              ['3', 'Pilot / Licensing / Investment Route', 'Choose the right path: technical pilot, licensing review, co-development, prime teaming, grant route, or qualified investor discussion.'],
              ['4', 'Legal and Execution Setup', 'Use NDA, partner agreement, data-sharing boundaries, or securities documentation where appropriate.'],
            ].map(([num, title, copy]) => (
              <article key={num} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-cyan-300 text-lg font-black text-slate-950">{num}</div>
                <h3 className="text-lg font-bold text-white">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="strategic-inquiry" className="bg-gradient-to-b from-blue-950/30 to-slate-950 px-6 py-20 md:py-28">
        <div className="container mx-auto">
          <SectionHeader eyebrow="Inquiry" title="Request a Strategic Partner Briefing">
            Use this form to draft a non-sensitive email inquiry. Do not send payment information, SSNs, bank details, investor verification documents, crypto wallets, dates of birth, classified information, CUI, or proprietary BOMs through this public page.
          </SectionHeader>

          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
            <aside className={`${glassCard} p-6`}>
              <LockKeyhole className="mb-5 text-cyan-300" size={34} aria-hidden="true" />
              <h3 className="text-2xl font-bold text-white">Public-safe mailto workflow</h3>
              <p className="mt-4 leading-7 text-slate-300">
                The site composes an email to SALAR Technologies LLC and does not collect sensitive investor verification data in the browser.
              </p>
              <a href={mailtoHref} className="mt-6 inline-flex items-center gap-2 rounded-xl border border-cyan-300/30 bg-cyan-300/10 px-5 py-3 font-semibold text-cyan-100 hover:bg-cyan-300/20">
                Draft email directly <Mail size={18} aria-hidden="true" />
              </a>
            </aside>

            <form onSubmit={handleSubmit} className={`${glassCard} p-6 md:p-8`}>
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="fullName" className="mb-2 block text-sm font-semibold text-cyan-100">Name</label>
                  <input id="fullName" name="fullName" required className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white" />
                </div>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-semibold text-cyan-100">Email</label>
                  <input id="email" name="email" type="email" required className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white" />
                </div>
                <div>
                  <label htmlFor="organization" className="mb-2 block text-sm font-semibold text-cyan-100">Organization</label>
                  <input id="organization" name="organization" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white" />
                </div>
                <div>
                  <label htmlFor="roleTitle" className="mb-2 block text-sm font-semibold text-cyan-100">Role / title</label>
                  <input id="roleTitle" name="roleTitle" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white" />
                </div>
                <div>
                  <label htmlFor="inquiryType" className="mb-2 block text-sm font-semibold text-cyan-100">Inquiry type</label>
                  <select id="inquiryType" name="inquiryType" required className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white">
                    <option value="">Select one</option>
                    <option>Strategic industrial partner</option>
                    <option>Pilot customer</option>
                    <option>Corporate venture / investor</option>
                    <option>Prime / integrator teaming</option>
                    <option>Licensing / IP commercialization</option>
                    <option>Grant / testbed / research partner</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="industryInterest" className="mb-2 block text-sm font-semibold text-cyan-100">Industry interest</label>
                  <select id="industryInterest" name="industryInterest" required className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white">
                    <option value="">Select one</option>
                    <option>Defense / aerospace</option>
                    <option>Semiconductor / advanced packaging</option>
                    <option>AI datacenter / cloud</option>
                    <option>Industrial / critical infrastructure</option>
                    <option>Automotive / EV / energy</option>
                    <option>Telecom / networking / edge</option>
                    <option>Consumer electronics</option>
                    <option>Enterprise AI / regulated workflows</option>
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="message" className="mb-2 block text-sm font-semibold text-cyan-100">Message</label>
                <textarea id="message" name="message" rows="6" className="w-full rounded-xl border border-cyan-300/20 bg-slate-950/70 px-4 py-3 text-white" />
              </div>

              <label className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-300/20 bg-amber-300/10 p-4 text-sm leading-6 text-amber-100">
                <input type="checkbox" required className="mt-1 h-4 w-4 flex-shrink-0 accent-cyan-300" />
                <span>I understand this is an inquiry-only form. It is not an offer to sell securities or a solicitation to buy securities.</span>
              </label>

              <Button type="submit" size="lg" className="mt-7 w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 md:w-auto">
                Request Partner Briefing <ArrowRight className="ml-2" size={18} aria-hidden="true" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="px-6 py-12">
        <div className="container mx-auto">
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 text-sm leading-7 text-slate-300 md:p-8">
            <h2 className="mb-3 text-lg font-bold text-white">Legal Note</h2>
            <p>{legalNote}</p>
          </div>
        </div>
      </section>
    </main>
  );
};

const CheckIcon = ShieldCheck;

export default InvestorRelations;
